import { Component, EventEmitter, HostBinding, Input, OnChanges, Output } from '@angular/core';

interface CalendarDay {
  date: Date;
  day: number;
  inCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isRangeStart: boolean;
  isRangeEnd: boolean;
  isInRange: boolean;
  isDisabled: boolean;
}

@Component({
  selector: 'app-custom-calendar',
  templateUrl: './custom-calendar.component.html',
  styleUrls: ['./custom-calendar.component.scss'],
})
export class CustomCalendarComponent implements OnChanges {
  @Input() mode: 'single' | 'range' = 'single';
  @Input() selectedDate: Date | null = null;
  @Input() rangeStart: Date | null = null;
  @Input() rangeEnd: Date | null = null;
  @Input() minDate: Date | null = null;
  @Input() showTime = false;
  @Input() align: 'left' | 'right' = 'left';
  @Input() verticalAlign: 'below' | 'above' = 'below';

  @HostBinding('class.align-right')
  get isAlignRight(): boolean {
    return this.align === 'right';
  }

  @HostBinding('class.align-up')
  get isAlignUp(): boolean {
    return this.verticalAlign === 'above';
  }

  @Output() dateChange = new EventEmitter<Date>();
  @Output() rangeChange = new EventEmitter<{ start: Date; end: Date }>();
  @Output() cancel = new EventEmitter<void>();

  weekdayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  viewMonth = new Date().getMonth();
  viewYear = new Date().getFullYear();
  calendarDays: CalendarDay[] = [];

  pendingDate: Date | null = null;
  pendingRangeStart: Date | null = null;
  pendingRangeEnd: Date | null = null;

  pendingHour = 12;
  pendingMinute = 0;
  pendingMeridiem: 'AM' | 'PM' = 'AM';
  openTimeDropdown: 'hour' | 'minute' | 'meridiem' | null = null;
  hoursList = Array.from({ length: 12 }, (_, i) => i + 1);
  minutesList = Array.from({ length: 60 }, (_, i) => i);
  meridiemList: ('AM' | 'PM')[] = ['AM', 'PM'];

  ngOnChanges(): void {
    if (this.mode === 'range') {
      this.pendingRangeStart = this.rangeStart || null;
      this.pendingRangeEnd = this.rangeEnd || null;
      const anchor = this.pendingRangeStart || new Date();
      this.viewMonth = anchor.getMonth();
      this.viewYear = anchor.getFullYear();
    } else {
      this.pendingDate = this.selectedDate || null;
      const anchor = this.pendingDate || new Date();
      this.viewMonth = anchor.getMonth();
      this.viewYear = anchor.getFullYear();
      if (this.showTime) {
        const source = this.pendingDate || new Date(0, 0, 0, 0, 0);
        const hours = source.getHours();
        this.pendingMeridiem = hours >= 12 ? 'PM' : 'AM';
        const hour12 = hours % 12;
        this.pendingHour = hour12 === 0 ? 12 : hour12;
        this.pendingMinute = source.getMinutes();
      }
    }
    this.buildCalendarGrid();
  }

  toggleTimeDropdown(name: 'hour' | 'minute' | 'meridiem'): void {
    this.openTimeDropdown = this.openTimeDropdown === name ? null : name;
  }

  selectHour(hour: number): void {
    this.pendingHour = hour;
    this.openTimeDropdown = null;
  }

  selectMinute(minute: number): void {
    this.pendingMinute = minute;
    this.openTimeDropdown = null;
  }

  selectMeridiem(meridiem: 'AM' | 'PM'): void {
    this.pendingMeridiem = meridiem;
    this.openTimeDropdown = null;
  }

  onPanelClick(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    if (!target.closest('.cal-time-dd')) {
      this.openTimeDropdown = null;
    }
  }

  get viewLabel(): string {
    return new Date(this.viewYear, this.viewMonth, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  prevMonth(): void {
    this.viewMonth--;
    if (this.viewMonth < 0) {
      this.viewMonth = 11;
      this.viewYear--;
    }
    this.buildCalendarGrid();
  }

  nextMonth(): void {
    this.viewMonth++;
    if (this.viewMonth > 11) {
      this.viewMonth = 0;
      this.viewYear++;
    }
    this.buildCalendarGrid();
  }

  private isSameDay(a: Date | null, b: Date | null): boolean {
    if (!a || !b) return false;
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  private buildCalendarGrid(): void {
    const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
    const startOffset = firstOfMonth.getDay();
    const gridStart = new Date(this.viewYear, this.viewMonth, 1 - startOffset);
    const today = new Date();
    const minDateOnly = this.minDate
      ? new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate())
      : null;

    const days: CalendarDay[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      const isDisabled = !!minDateOnly && date < minDateOnly;

      let isSelected = false;
      let isRangeStart = false;
      let isRangeEnd = false;
      let isInRange = false;

      if (this.mode === 'range') {
        isRangeStart = this.isSameDay(date, this.pendingRangeStart);
        isRangeEnd = this.isSameDay(date, this.pendingRangeEnd);
        if (this.pendingRangeStart && this.pendingRangeEnd) {
          isInRange = date > this.pendingRangeStart && date < this.pendingRangeEnd;
        }
      } else {
        isSelected = this.isSameDay(date, this.pendingDate);
      }

      days.push({
        date,
        day: date.getDate(),
        inCurrentMonth: date.getMonth() === this.viewMonth,
        isToday: this.isSameDay(date, today),
        isSelected,
        isRangeStart,
        isRangeEnd,
        isInRange,
        isDisabled,
      });
    }
    this.calendarDays = days;
  }

  selectDay(day: CalendarDay): void {
    if (day.isDisabled) return;

    if (this.mode === 'range') {
      if (!this.pendingRangeStart || (this.pendingRangeStart && this.pendingRangeEnd)) {
        this.pendingRangeStart = day.date;
        this.pendingRangeEnd = null;
      } else if (day.date < this.pendingRangeStart) {
        this.pendingRangeStart = day.date;
      } else {
        this.pendingRangeEnd = day.date;
      }
    } else {
      this.pendingDate = day.date;
    }

    if (!day.inCurrentMonth) {
      this.viewMonth = day.date.getMonth();
      this.viewYear = day.date.getFullYear();
    }
    this.buildCalendarGrid();
  }

  get canApply(): boolean {
    if (this.mode === 'range') {
      return !!this.pendingRangeStart && !!this.pendingRangeEnd;
    }
    return !!this.pendingDate;
  }

  apply(): void {
    if (!this.canApply) return;

    if (this.mode === 'range') {
      this.rangeChange.emit({ start: this.pendingRangeStart as Date, end: this.pendingRangeEnd as Date });
    } else {
      let result = this.pendingDate as Date;
      if (this.showTime) {
        let hour24 = this.pendingHour % 12;
        if (this.pendingMeridiem === 'PM') hour24 += 12;
        result = new Date(result.getFullYear(), result.getMonth(), result.getDate(), hour24, this.pendingMinute);
      }
      this.dateChange.emit(result);
    }
  }

  cancelPick(): void {
    this.openTimeDropdown = null;
    this.cancel.emit();
  }
}
